<template>
  <div class="editor-container">
    <div class="editor-header">
      <div class="widget-type">
        <router-link
          :to="{ name: 'pdf_report_editor', params: { report_id: reportId } }">
          <b-button
            type="is-light"
            class="back-btn mr-5"
            icon-right="arrow-left" />
        </router-link>
        <div class="report-breadcrumbs">
          <div>
            <h4>Report</h4>
            <h2>{{ reportData?.name || "Pdf Report" }}</h2>
          </div>

          <div class="arrow-right">
            <i class="material-icons">chevron_right </i>
          </div>

          <div>
            <h4>Module</h4>
            <h2>{{ module?.title || moduleConfigs.name }}</h2>
          </div>
        </div>
      </div>

      <div class="controls">
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          hotkeys="ctrl+shift+d"
          class="button is-light"
          @click="getData">
          Discard Changes
        </a-button>
        <a-button
          type="button ml-2"
          :disabled="!isDirty"
          :loading="isSaving"
          hotkeys="ctrl+shift+s"
          class="button is-primary"
          @click="save">
          Publish Changes
        </a-button>
      </div>
    </div>
    <div class="editor-body">
      <div class="block layout">
        <div class="block-header">
          <h4>KPIs</h4>
        </div>

        <div
          v-if="module"
          class="block-body">
          <b-tooltip
            style="width: 100%"
            position="is-right"
            type="is-light"
            :append-to-body="true"
            multilined>
            <div
              class="row mini-view-item"
              :class="{
                selected: selection === null,
                'error-border': moduleSettingsErrors.length > 0,
              }"
              @click="select(null)">
              <img
                :src="moduleConfigs.icon"
                alt="" />
              <div>{{ module?.title || moduleConfigs.name }}</div>
            </div>
            <template #content>
              <div class="data-tooltip">
                <div>
                  <h2>KPIs per row:</h2>
                  {{ module?.kpi_style?.columns_per_row }}
                </div>
                <div>
                  <h2>Measures:</h2>
                  {{ module?.kpi_measures?.length || 0 }}
                </div>
              </div>
            </template>
          </b-tooltip>

          <MiniKpiModule
            v-if="!isLoading && module"
            :key="module.key"
            v-model="module"
            :selection="selection"
            :measure-errors="kpiMeasureErrors"
            :primary-row-errors="primaryRowErrors"
            :benchmark-errors="benchmarkErrors"
            @open="select" />
        </div>
      </div>
      <div class="editor-and-preview">
        <splitpanes class="default-theme">
          <pane
            min-size="20"
            class="pane">
            <div class="block editor">
              <KpiMeasure
                v-if="selection?.kind === 'measure'"
                :key="`measure-${selection.index}`"
                v-model="module.kpi_measures[selection.index]"
                :errors="selectedErrors"
                :context="rowContext"
                @exclusive-hero="clearOtherHeroes(selection.index)" />
              <KpiRow
                v-else-if="selection?.kind === 'primary'"
                key="primary-row"
                v-model="module.primary_row"
                :errors="selectedErrors"
                :context="rowContext"
                :is-primary="true" />
              <KpiBenchmark
                v-else-if="selection?.kind === 'benchmark'"
                :key="`benchmark-${selection.index}`"
                v-model="module.benchmarks[selection.index]"
                :errors="selectedErrors"
                :context="rowContext" />
              <KpiModuleEditor
                v-else
                :key="module?.key"
                v-model="module"
                :errors="moduleSettingsErrors"
                :report-default-time-range="reportData?.default_time_range" />
            </div>
          </pane>
          <pane min-size="20">
            <PreviewAndJSONEditor
              v-model="module"
              type="module"
              :full-report="reportData"
              :default-time-range="reportData?.default_time_range"
              @validation-errors="moduleErrors = $event" />
          </pane>
        </splitpanes>
      </div>
    </div>
    <b-loading
      v-model="isLoading"
      :is-full-page="true"
      :can-cancel="false" />

    <b-modal
      v-model="showUnsavedModal"
      :destroy-on-hide="true"
      trap-focus
      close-button-aria-label="Close"
      aria-modal
      custom-class="unsaved-modal-simple">
      <div class="unsaved-modal-content">
        <div class="unsaved-modal-title-row">
          <span class="unsaved-modal-title">You have unsaved changes</span>
        </div>
        <div class="unsaved-modal-body">
          <p>Do you want to save your changes before leaving this page?</p>
        </div>
        <div class="unsaved-modal-actions custom-unsaved-modal-actions">
          <b-button
            type="is-outlined"
            size="is-small"
            class="cancel-btn"
            @click="closeUnsavedModal">
            Cancel
          </b-button>
          <div class="right-actions">
            <b-button
              type="is-muted"
              size="is-small"
              class="discard-btn"
              @click="discardAndNavigate">
              Discard
            </b-button>
            <b-button
              size="is-small"
              class="save-btn"
              @click="saveAndNavigate">
              Save Changes
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import MiniKpiModule from '@/components/pdf_report/MiniKpiModule.vue'
  import KpiModuleEditor from '@/components/pdf_report/kpi_module/KpiModuleEditor.vue'
  import KpiMeasure from '@/components/pdf_report/kpi_module/KpiMeasure.vue'
  import KpiRow from '@/components/pdf_report/kpi_module/KpiRow.vue'
  import KpiBenchmark from '@/components/pdf_report/kpi_module/KpiBenchmark.vue'
  import uniqid from 'uniqid'
  import { Splitpanes, Pane } from 'splitpanes'
  import PreviewAndJSONEditor from '@/components/pdf_report/PreviewAndJsonEditor.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  // Which part of the module the right-hand panel is editing. `null` is the
  // module's own settings.
  const SELECTION_ERROR_ROOTS = {
    measure: 'kpi_measures',
    primary: 'primary_row',
    benchmark: 'benchmarks',
  }

  export default {
    name: 'KpiEditorPage',
    components: {
      MiniKpiModule,
      KpiModuleEditor,
      KpiMeasure,
      KpiRow,
      KpiBenchmark,
      Splitpanes,
      Pane,
      PreviewAndJSONEditor,
    },
    beforeRouteLeave (to, from, next) {
      if (this.isDirty) {
        this.showUnsavedModal = true
        this.nextRoute = { to, from, }
        this.nextRouteCallback = next
      } else {
        next()
      }
    },
    props: {},
    data () {
      return {
        moduleId: null,
        reportId: null,
        module: null,
        reportData: null,

        moduleConfigs: {
          name: 'KPIs',
          type: 'KpiModule',
          icon: require('@/assets/icons/kpi.svg'),
        },

        selection: null,
        moduleErrors: [],

        isLoading: false,
        isDirty: false,
        isSaving: false,
        showUnsavedModal: false,
        nextRoute: null,
        nextRouteCallback: null,
      }
    },

    computed: {
      moduleDefaultTimeRange () {
        return this.module?.default_time_range ||
          this.reportData?.default_time_range || null
      },
      rowContext () {
        return {
          moduleDefaultTimeRange: this.moduleDefaultTimeRange,
          timeRangeDefaultLabel: 'Use module default',
          timeRangeDefaultName: this.moduleDefaultTimeRange?.name || '',
          // Rows are templates applied to every measure, so the row editor
          // needs the measures to say which direction it will be read in.
          kpiMeasures: this.module?.kpi_measures || [],
        }
      },
      moduleSettingsErrors () {
        return this.moduleErrors.filter(e => {
          const root = e.loc?.[0]
          return !Object.values(SELECTION_ERROR_ROOTS).includes(root)
        })
      },
      kpiMeasureErrors () {
        return this.childErrors('kpi_measures', this.moduleErrors)
      },
      primaryRowErrors () {
        return this.childErrors('primary_row', this.moduleErrors)
      },
      benchmarkErrors () {
        return this.childErrors('benchmarks', this.moduleErrors)
      },
      selectedErrors () {
        if (this.selection === null) return []
        if (this.selection.kind === 'primary') return this.primaryRowErrors
        if (this.selection.kind === 'measure') {
          return this.childErrors(this.selection.index, this.kpiMeasureErrors)
        }
        return this.childErrors(this.selection.index, this.benchmarkErrors)
      },
    },
    watch: {
      module: {
        handler () {
          this.isDirty = true
        },
        deep: true,
      },
    },

    async mounted () {
      this.moduleId = this.$route.params.module_id
      this.reportId = this.$route.params.report_id

      await Promise.all([
        this.getData(),
        this.loadCompanyConfigs(),
      ])

      if (!this.module.key) {
        this.module.key = uniqid()
      }

      this.isLoading = false
    },
    methods: {
      childErrors: _childErrors,
      async loadCompanyConfigs () {
        await this.$store.dispatch('app/reportsConfig/refreshOptions')
      },

      async getData () {
        this.isLoading = true
        this.moduleErrors = []
        const response = await this.$apis.pdfReports.getPdfModuleData(
          this.moduleId
        )
        this.module = response

        this.reportData = await this.$apis.pdfReports.getPdfReportData(
          this.reportId
        )

        await this.$nextTick()
        this.isDirty = false
        this.isLoading = false
      },

      closeUnsavedModal () {
        this.showUnsavedModal = false
        this.nextRoute = null
        this.nextRouteCallback = null
      },

      discardAndNavigate () {
        this.showUnsavedModal = false
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
      },

      async saveAndNavigate () {
        await this.save()
        if (this.nextRouteCallback) {
          this.nextRouteCallback()
        }
      },

      async save () {
        this.isSaving = true
        this.isDirty = false
        const response = await this.$apis.pdfReports.updatePdfReportModule(
          this.module
        )
        this.isSaving = false

        if (!response) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Backend Error, Report this to the devs',
            type: 'is-danger',
          })
          return
        }

        if (response?.errors) {
          this.isDirty = true
          this.$buefy.toast.open({
            message: 'Error saving module, Fix the errors and try again',
            type: 'is-warning',
          })
          this.moduleErrors = response.errors
        } else {
          this.module = response
          this.moduleErrors = []
          this.$buefy.toast.open({
            message: 'Changes Saved',
            type: 'is-success',
            position: 'is-bottom',
          })

          await this.$nextTick()
          await this.$nextTick()
          this.isDirty = false
        }
      },

      select (selection = null) {
        this.selection = selection
      },

      clearOtherHeroes (index) {
        // The module owns "one headline", not the measure -- a measure can
        // only turn itself on, so something above has to turn the others off.
        this.module.kpi_measures.forEach((measure, i) => {
          if (i !== index && measure.is_hero) {
            this.$set(measure, 'is_hero', false)
          }
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
.data-tooltip {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;

  hr {
    margin: 5px 0;
    border: 0;
    border-top: 1px solid black;
  }
  h2 {
    font-size: 12px;
    font-weight: 600;
  }
}
.report-breadcrumbs {
  display: flex;

  h2 {
    font-weight: 400;
    font-size: 20px;
    margin: 0;
  }

  h4 {
    font-weight: 400;
    font-size: 12px;
    margin: 0;
    color: $grayscale-3;
  }

  .arrow-right {
    display: flex;
    align-items: center;
    margin: 0 10px;
    font-size: 20px;
    color: #b0b0b0;
  }
}
.back-btn {
  font-size: 20px !important;
  border-radius: 100% !important;
}
.editor-container {
  padding: 0 20px 5px;
  width: 100%;
  height: 100%;
  margin: auto !important;
  background-color: white;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden !important;
}

.editor-body {
  display: flex;
  max-height: 100%;
  margin: 0 !important;
  overflow: hidden !important;
}

.block {
  border: $border-1;
  border-radius: 3px;
  height: 100%;
  flex-direction: column;
  display: flex;

  .block-header {
    min-height: 40px;
    align-items: center;
    padding: 0 5px;
    border-bottom: $border-1;
    background-color: white;
    display: flex;

    h4 {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      padding: 0 10px;
      text-transform: capitalize;
      margin: 0;
      color: black;
    }
  }
  .block-body {
    padding: 10px 10px;
    background-color: #fbfbfb;
    height: 100%;
    width: 100%;
    flex-direction: column;
    display: flex;
  }

  &.layout {
    max-height: 100%;
    overflow: hidden !important;
    margin-right: 10px;
    min-width: 300px;
    margin-bottom: 0 !important;

    .block-body {
      overflow-y: auto;
    }
  }

  &.editor {
    margin: 0 !important;
  }

  &.preview {
    height: 100%;
    .block-header {
      width: 100%;
    }

    .block-body {
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .output {
      background-color: white;
      height: 100%;
      overflow: auto;
    }
  }
}

.editor-and-preview {
  width: 100%;
}

.editor-header {
  margin-bottom: 20px;

  display: flex;
  padding-top: 10px;
  .widget-type {
    display: flex;
    font-weight: 400;
    font-size: 15px;
    align-items: center;
    img {
      width: 35px;
      height: 35px;
      margin-right: 10px;
      border: $border-1;
      border-radius: 8px;
      padding: 5px;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .controls {
    margin-left: auto;
    display: flex;
    align-items: center;

    .button {
      padding: 0 15px;
      font-size: 12px;
    }
  }
}

.mini-view-item {
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, color 0.3s;
  width: 100%;
  padding: 10px;
  display: flex;
  border-radius: 4px;
  align-items: center;
  font-size: 15px;
  background-color: white;
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 4px 0px rgba(40, 39, 43, 0.02),
    0px 1px 2px 0px rgba(40, 39, 43, 0.06);
  &.add-button {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
    border: $border-1;
    display: flex;
    align-items: center;
  }
  img {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
  }
  &.selected {
    background-color: $primary;
    color: white;
    img {
      filter: brightness(0) invert(1);
    }
  }
}

.unsaved-modal-simple {
  .modal-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
.unsaved-modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px 24px 16px 24px;
  min-width: 320px;
  max-width: 400px;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.unsaved-modal-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.unsaved-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #222;
}
.unsaved-modal-close {
  background: none;
  border: none;
  font-size: 22px;
  color: #888;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.unsaved-modal-body {
  font-size: 15px;
  color: #444;
  margin-bottom: 18px;
}
.unsaved-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.custom-unsaved-modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0;
  padding-top: 4px;
}
.custom-unsaved-modal-actions .cancel-btn {
  min-width: 90px;
  border-radius: 4px !important;
  color: black !important;
  font-size: 14px !important;
  border: 1px solid #e0e0e0 !important;
}
.custom-unsaved-modal-actions .right-actions {
  display: flex;
  gap: 8px;
}
.custom-unsaved-modal-actions .discard-btn {
  background: #f5f5f5 !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
.custom-unsaved-modal-actions .save-btn {
  background: #111 !important;
  color: #fff !important;
  border: 1px solid #111 !important;
  border-radius: 4px !important;
  font-size: 14px !important;
}
</style>
